import React, { useState } from 'react';
import './Services.css';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import entIcon from '../assets/HomeIcons/Enterprises.webp';
import entIconM from '../assets/HomeIcons/Enterprises-100w.webp';
import indIcon from '../assets/HomeIcons/Industrial.webp';
import indIconM from '../assets/HomeIcons/Industrial-100w.webp';
import customIcon from '../assets/HomeIcons/custom software.webp';
import customIconM from '../assets/HomeIcons/custom software-100w.webp';
import cloudIcon from '../assets/HomeIcons/cloud services.webp';
import cloudIconM from '../assets/HomeIcons/cloud services-100w.webp';
interface ServicesProps {
  preview?: boolean;
}

export const Services: React.FC<ServicesProps> = ({ preview = false }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoverTimeout, setHoverTimeout] = useState<number | null>(null);

  const handleMouseEnter = (idx: number) => {
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }

    // Set a small delay before expanding the card
    const timeout = setTimeout(() => {
      setActiveIndex(idx);
    }, 150); // 150ms delay to prevent vibration

    setHoverTimeout(timeout);
  };

  const handleMouseLeave = () => {
    // Clear timeout when leaving the panel
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  const handleClick = (idx: number) => {
    // Clear any pending hover timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    // Immediately set active on click
    setActiveIndex(idx);
  };

  const servicesList = [
    {
      title: 'Enterprise Software Solutions',
      desc: 'We build secure, scalable, and reliable enterprise software solutions that help businesses simplify operations, improve collaboration, and increase productivity. Our custom applications are designed to meet your unique business requirements and support long-term growth.',
      icon: <img loading="lazy" src={entIcon} srcSet={`${entIconM} 100w, ${entIcon} 200w`} sizes="38px" alt="Enterprise Software" style={{ width: 38, height: 38, objectFit: 'contain' }}  title="Vivify Soft Tech" />,
      features: ['ERP Systems', 'CRM Software', 'HRMS Portals', 'Workflow Engines'],
      accent: '#6C63FF',
      link: '/services/enterprise-software-solutions',
    },
    {
      title: 'Industrial Automation Service',
      desc: 'We help industries improve efficiency, productivity, and operational performance through intelligent automation solutions. Our services are designed to automate processes, reduce manual effort, and ensure smooth, reliable operations across manufacturing and industrial environments in Chennai.',
      icon: <img loading="lazy" src={indIcon} srcSet={`${indIconM} 100w, ${indIcon} 200w`} sizes="38px" alt="Industrial Automation" style={{ width: 38, height: 38, objectFit: 'contain' }}  title="Vivify Soft Tech" />,
      features: ['Industrial IoT', 'Smart Factories', 'OEE Monitoring', 'Production Audits'],
      accent: '#6C63FF',
      link: '/services/industrial-software-automation',
    },
    {
      title: 'Custom Product Development',
      desc: 'We transform ideas into powerful digital products that solve real business challenges. From concept and design to development and deployment, we build custom software products tailored to your goals, users, and industry requirements.',
      icon: <img loading="lazy" src={customIcon} srcSet={`${customIconM} 100w, ${customIcon} 200w`} sizes="38px" alt="Custom Product Development" style={{ width: 38, height: 38, objectFit: 'contain' }}  title="Vivify Soft Tech" />,
      features: ['SaaS Architecture', 'Rapid MVPs', 'Mobile Frameworks', 'REST/gRPC APIs'],
      accent: '#6C63FF',
      link: '/services/custom-product-development',
    },
    {
      title: 'Cloud & IT Services',
      desc: "We provide secure and scalable cloud and IT services that help businesses improve performance, reduce costs, and stay connected. Whether you're moving to the cloud, managing IT infrastructure, or improving system security, our experts deliver solutions that keep your business running smoothly.",
      icon: <img loading="lazy" src={cloudIcon} srcSet={`${cloudIconM} 100w, ${cloudIcon} 200w`} sizes="38px" alt="Cloud & IT Services" style={{ width: 38, height: 38, objectFit: 'contain' }}  title="Vivify Soft Tech" />,
      features: ['Cloud Migration', 'Managed Services', 'DevOps & CI/CD', 'Zero-Trust Security'],
      accent: '#6C63FF',
      link: '/services/cloud-it-services',
    },
  ];

  return (
    <section className="section services-section" id="services">
      <div className="container relative-z">
        <div className={`services-layout ${preview ? 'services-layout-preview' : ''}`}>

          {/* ── LEFT COLUMN ── */}
          <div className="services-left">
            <div className="services-eyebrow">
              <span className="services-eyebrow-tag">SERVICES</span>
              <span className="services-eyebrow-line"></span>
            </div>

            <h2 className="services-main-title">
              Intelligent Technology Solutions
            </h2>

            <p className="services-lead-text">
              At Vivify Soft Tech, we deliver intelligent technology solutions that help businesses innovate, automate, and grow in a rapidly evolving digital world. By combining advanced technologies with industry expertise, we build scalable, secure, and high-performance solutions tailored to your unique business needs.
            </p>

            <div className="services-action-container">
              <Link to="/services">
                <Button variant="primary" size="md" icon={<ArrowRight size={16} />} className="about-hero-primary-btn">
                  Explore All Services
                </Button>
              </Link>
            </div>
          </div>

          {/* ── RIGHT COLUMN: Accordion panels ── */}
          {!preview && (
            <div className="services-accordion">
              {servicesList.map((service, idx) => (
                <div
                  key={idx}
                  className={`svc-panel${activeIndex === idx ? ' svc-panel--active' : ''}`}
                  onClick={() => handleClick(idx)}
                  onMouseEnter={() => handleMouseEnter(idx)}
                  onMouseLeave={handleMouseLeave}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setActiveIndex(idx)}
                  aria-expanded={activeIndex === idx}
                  style={{ '--panel-accent': service.accent } as React.CSSProperties}
                >
                  <div className="svc-panel-num-wrapper">
                    <span className="svc-panel-num">
                      {String(idx + 1).padStart(3, '0')}
                    </span>
                  </div>

                  <span className="svc-panel-vertical-title">{service.title}</span>

                  <div className="svc-panel-content">
                    <div className="svc-panel-header-row">
                      <div className="svc-panel-icon">{service.icon}</div>
                      <h3 className="svc-panel-title">{service.title}</h3>
                    </div>
                    <p className="svc-panel-desc">{service.desc}</p>

                    <ul className="svc-panel-features">
                      {service.features.map((feat, fi) => (
                        <li key={fi} className="svc-panel-feat">{feat}</li>
                      ))}
                    </ul>

                    <Link
                      to={service.link}
                      className="svc-panel-cta"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Learn More <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </section>
  );
};

export default Services;
